import { lazy, Suspense, useEffect, useState } from "react";
import "./index.css";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import { NAV_LINKS } from "./data";

const About = lazy(() => import("./components/About"));
const Skills = lazy(() => import("./components/Skills"));
const Experience = lazy(() => import("./components/Experience"));
const Projects = lazy(() => import("./components/Projects"));
const Journey = lazy(() => import("./components/Journey"));
const Education = lazy(() => import("./components/Education"));
const Blog = lazy(() => import("./components/Blog"));
const Contact = lazy(() => import("./components/Contact"));
const Footer = lazy(() => import("./components/Footer"));

const SectionFallback = () => (
  <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
    <div className="h-44 rounded-[2rem] border border-slate-800/70 bg-slate-900/80 animate-pulse" />
  </div>
);

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  // Sticky nav background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section tracker — the active section is the last one whose
  // top edge has scrolled above the 120px nav-height offset line.
  useEffect(() => {
    const ids = ["hero", ...NAV_LINKS.map((l) => l.toLowerCase())];

    const onScroll = () => {
      const offset = 120;
      let current = "hero";
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = id;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    // Close first so body scroll-lock cleanup fires and restores position
    // before scrollIntoView executes — prevents a scroll-position race.
    setMenuOpen(false);
    requestAnimationFrame(() => {
      const target = document.getElementById(id.toLowerCase());
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Nav
        scrolled={scrolled}
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((v) => !v)}
        onNavigate={scrollTo}
        activeSection={activeSection}
      />

      <main className="overflow-hidden">
        <Hero onNavigate={scrollTo} />

        <Suspense fallback={<SectionFallback />}>
          <About />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Skills />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Experience />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Projects />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Journey />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Education />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Blog />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Contact />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Footer />
        </Suspense>
      </main>
    </div>
  );
}
